import { createContext, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { onIdTokenChanged } from "firebase/auth";
import { auth } from "../firebases";

const UserContext = createContext();

export function UserProvider({ children }) {
  const [userData, setUserData] = useState(() => {
    const stored = localStorage.getItem("userData");
    return stored ? JSON.parse(stored) : null;
  });
  const [licenses, setLicenses] = useState(() => {
    const stored = localStorage.getItem("licenses");
    return stored ? JSON.parse(stored) : [];
  });
  const [posData, setPosData] = useState(() => {
    const stored = localStorage.getItem("posData");
    return stored ? JSON.parse(stored) : [];
  });
  const [posPidArray, setPosPidArray] = useState(() => {
    const stored = localStorage.getItem("posPidArray");
    return stored ? JSON.parse(stored) : [];
  });
  const [idToken, setIdToken] = useState(() => {
    const stored = localStorage.getItem("idToken");
    return stored ? stored : null;
  });
  const [hierarchyData, setHierarchyData] = useState(() => {
    const stored = localStorage.getItem("hierarchyData");
    return stored ? JSON.parse(stored) : null;
  });
  const [isLoadingHierarchy, setIsLoadingHierarchy] = useState(false);
  const [role, setRole] = useState(() => {
    return localStorage.getItem("userRole") || null;
  });

  // 當 userData 變更時，儲存到 localStorage
  useEffect(() => {
    if (userData !== null) {
      localStorage.setItem("userData", JSON.stringify(userData));
      console.log("Saved userData to localStorage:", userData);
    } else {
      localStorage.removeItem("userData");
      console.log("Removed userData from localStorage");
    }
  }, [userData]);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user) {
        const token = await user.getIdToken(); // 自動刷新 token
        setIdToken(token);
        localStorage.setItem("idToken", token);
        // console.log("ID Token refreshed:", token);
      } else {
        setIdToken(null);
        localStorage.removeItem("idToken");
        console.log("User signed out, token removed");
      }
    });

    return () => unsubscribe();
  }, []);

  // 當 role 變更時，儲存到 localStorage
  useEffect(() => {
    if (role !== null) {
      localStorage.setItem("userRole", role);
      console.log("Saved role to localStorage:", role);
    } else {
      localStorage.removeItem("userRole");
      console.log("Removed role from localStorage");
    }
  }, [role]);

  // 從 CRM API 獲取 role
  const fetchUserRole = async (email) => {
    try {
      const crmApiUrl =
        "https://crm-mqtt-718408119422.asia-east1.run.app/api/v1/contact/list?sortfield=t.rowid&sortorder=ASC&limit=100";
      const response = await fetch(crmApiUrl, {
        method: "GET",
        headers: {
          "Mdm-Auth": "57b2a9ba-4428-48e4-b137-f8337b68595e",
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch data from CRM API");
      }

      const crmData = await response.json();
      // console.log("CRM API response:", crmData.data);

      if (crmData.code !== 200 || !crmData.data) {
        throw new Error("Invalid response from CRM API");
      }

      const userContact = crmData.data.find((contact) => contact.email === email);

      if (userContact) {
        const userRole = userContact.note_public;
        console.log(`Role fetched from CRM: ${userRole}`);
        setRole(userRole);
        return userRole;
      } else {
        console.warn("User email not found in CRM contact list.");
        setRole("default");
        return "default";
      }
    } catch (error) {
      console.error("Error fetching role:", error.message);
      setRole("default");
      return "default";
    }
  };

  // 從 Firestore 獲取 userData
  const fetchUserData = async (email) => {
    try {
      const db = getFirestore();
      const userDocRef = doc(db, "users", email);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const data = userDocSnap.data();
        console.log("User data fetched from Firestore:", data);
        setUserData(data);
        return data;
      } else {
        console.warn("User data not found in Firestore for email:", email);
        setUserData(null);
        return null;
      }
    } catch (error) {
      console.error("Error fetching user data:", error.message);
      setUserData(null);
      return null;
    }
  };

  return (
    <UserContext.Provider
      value={{
        userData,
        setUserData,
        licenses,
        setLicenses,
        posData,
        setPosData,
        posPidArray,
        setPosPidArray,
        idToken,
        setIdToken,
        hierarchyData,
        setHierarchyData,
        isLoadingHierarchy,
        setIsLoadingHierarchy,
        role,
        setRole,
        fetchUserRole,
        fetchUserData, // 新增 fetchUserData
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useUserContext() {
  return useContext(UserContext);
}
